import { html, css, nothing } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints, ApiError } from '../api/client.js';
import { navigate } from '../router.js';
import type { MfaMethodSummary } from '../api/types.js';
import type { SsoTextField } from '../components/sso-text-field.js';
import '../components/sso-card.js';
import '../components/sso-text-field.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';

/**
 * `<login-page>` — username + password, "stay signed in", forgot link, and (CRM-only)
 * a Microsoft sign-in button (SSO-MOCKUPS.md §1).
 *
 * The server validates, applies throttling and tells us the next step. On a terminal handoff it
 * returns `{ redirect }`; otherwise `{ next }` which the shell routes (choose / totp / enroll …).
 * Errors are GENERIC (no account enumeration, M4).
 */
@customElement('login-page')
@localized()
export class LoginPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      h1 {
        margin-bottom: 0.25rem;
      }
      .sub {
        margin-top: 0;
      }
      form {
        display: grid;
        gap: var(--sso-space);
      }
      .row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--sso-space);
        flex-wrap: wrap;
      }
      .stay {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--sso-color-fg);
        min-height: var(--sso-touch-target);
        cursor: pointer;
      }
      .divider {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        color: var(--sso-color-muted);
        font-size: 0.9rem;
        margin: 0.25rem 0;
      }
      .divider::before,
      .divider::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--sso-color-border);
      }
      .from {
        text-align: center;
        color: var(--sso-color-muted);
        font-size: 0.85rem;
        margin-top: var(--sso-space-lg);
      }
      a {
        font-size: 0.9rem;
      }
    `,
  ];

  @state() private error = '';
  @state() private busy = false;
  /** App that initiated the flow (from ?app=crm) — gates the Microsoft button to CRM. */
  @state() private fromApp = '';

  @query('sso-text-field[name="username"]') private usernameField?: SsoTextField;
  @query('sso-text-field[name="password"]') private passwordField?: SsoTextField;
  @query('#stay') private stayBox?: HTMLInputElement;

  override connectedCallback(): void {
    super.connectedCallback();
    const params = new URLSearchParams(location.search);
    this.fromApp = params.get('app') ?? '';
    // Stash the app-initiated return target NOW, so it survives the 2FA / enrollment navigations
    // (which drop the query string). Read back via resolveReturnUri() at each completion site.
    const ru = this.safeReturnUri(params.get('return_uri'));
    if (ru) {
      this.flowContext?.setReturnUri(ru);
      // A hash-routed app's deep link (#!/…) rode along in the URL fragment through the 302s that
      // brought us here; stash it too, so resolveReturnUri() can re-attach it (see sso-base).
      this.flowContext?.setReturnHash(this.safeReturnHash(location.hash));
    }
  }

  override firstUpdated(): void {
    // Focus the first field (username) on entry so the user can type straight away.
    this.focusFirstControl();
  }

  private async onSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    const username = (this.usernameField?.value ?? '').trim();
    const password = this.passwordField?.value ?? '';
    if (!username || !password) {
      this.error = msg('Please enter your username and password.');
      return;
    }

    const staySignedIn = this.stayBox?.checked ?? false;

    this.busy = true;
    this.error = '';
    try {
      const r = await endpoints.loginPassword(username, password, staySignedIn);
      switch (r.next) {
        case 'mfa':
          // Cache the methods so the choose-method page need not refetch.
          this.cacheMethods(r.methods);
          navigate('/2fa/choose');
          return;
        case 'enroll':
          navigate('/enroll/gate');
          return;
        case 'complete':
          // Fully authenticated. Honour a launcher-initiated return target if present.
          this.completeLogin();
          return;
        case 'failed':
        default:
          // GENERIC message — never reveal whether the username or password was wrong (M4).
          this.error = msg('Sign-in failed. Please check your details and try again.');
          return;
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        this.error = msg('Your account is temporarily locked. Please try again later.');
      } else {
        this.error = msg('Sign-in failed. Please check your details and try again.');
      }
    } finally {
      this.busy = false;
    }
  }

  /** Cache the password-step methods on the shell's flow context. */
  private cacheMethods(methods: MfaMethodSummary[]): void {
    this.flowContext?.setMethods(methods);
  }

  /**
   * After a fully-authenticated password step (no 2FA required) navigate onward. If the SSO login was
   * reached via a launcher-initiated authorize hop (`return_uri`), follow it via a real browser
   * navigation so the backend can 302 to the app; otherwise show the launcher.
   */
  private completeLogin(): void {
    const returnUri = this.resolveReturnUri();
    if (returnUri) {
      this.redirect(returnUri);
      return;
    }
    navigate('/');
  }

  private loginWithMicrosoft(): void {
    // Delegates to CRM's existing Entra/OIDC flow (bypasses SSO-2FA; Microsoft enforces its own MFA).
    this.redirect('/signin-oidc/crm');
  }

  override render() {
    return html`
      <sso-card>
        <h1>${msg('Sign in')}</h1>
        <p class="sub">${msg('Sign in to Pinakes')}</p>

        ${this.error ? html`<sso-alert kind="error">${this.error}</sso-alert>` : nothing}

        <form @submit=${this.onSubmit} novalidate>
          <sso-text-field
            name="username"
            label=${msg('Username')}
            autocomplete="username"
            required
            ?disabled=${this.busy}></sso-text-field>
          <sso-text-field
            name="password"
            type="password"
            label=${msg('Password')}
            autocomplete="current-password"
            required
            ?disabled=${this.busy}></sso-text-field>

          <div class="row">
            <label class="stay">
              <input id="stay" type="checkbox" name="staySignedIn" ?disabled=${this.busy} />
              ${msg('Stay signed in')}
            </label>
            <a href="/forgot-password">${msg('Forgot your password?')}</a>
          </div>

          <sso-button type="submit" variant="primary" ?busy=${this.busy}>
            ${msg('Sign in')}
          </sso-button>
        </form>

        ${this.fromApp === 'crm'
          ? html`
              <div class="divider">${msg('or')}</div>
              <sso-button variant="social" @click=${this.loginWithMicrosoft} ?disabled=${this.busy}>
                ${msg('Sign in with Microsoft')}
              </sso-button>
            `
          : nothing}

        ${this.fromApp
          ? html`<p class="from">${msg('Referred from:')} ${this.fromApp.toUpperCase()}</p>`
          : nothing}
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'login-page': LoginPage;
  }
}
